<template>
    <div class="flex mb-px draggable relative" :style="'background-color: '+item.color" :key="itemKey"
         @mouseover="highlight" @mouseleave="unhighlight">
        <!-- Transparent Cover on multimode -->
        <div class="absolute inset-0 -mt-px -mb-px z-20" v-show="this.multimode">
            <!-- Highlight if highlighted -->
            <div class="absolute inset-0 m-0 z-20 border-none bg-opacity-20 bg-red-300" v-show="this.selected"
                 @click="removeSelected">
            </div>
            <div class="absolute inset-0 m-0 z-20 border-none bg-opacity-10 bg-red-300 "
                 v-show="!this.selected && this.highlighted" @click="addSelected">
            </div>
        </div>

        <!-- Draggable handle. -->
        <div class="w-6 flex-shrink-0 py-1.5 -my-px border-t">
            <div class="w-5 h-5 flex items-center">
                <i class="fa fa-bars fa-fw text-xs text-gray-300 hover:text-gray-400 cursor-move pl-1 handle"
                   v-if="!estimate.readonly"></i>
            </div>
        </div>

        <!-- Reference. -->
        <div class="w-16 flex m-0 relative">
            <page-cell
                class="text-sm -my-px ml-0 px-2 py-1.5 border border-gray-200"
                :style="'background-color: '+item.color"
                :placeholder="item.defaultReference"
                :read-only="estimate.readonly"
                v-model="item.reference"
                @input="estimate.update()"
                @change="estimate.addSnapshot()"
                ref="reference"
            ></page-cell>
        </div>

        <!-- Item -->
        <div class="flex-1">
            <!-- Main Item -->
            <div class="flex">
                <!-- Description. -->
                <div class="flex-1 flex m-0 -ml-px relative">
                    <div
                        :key="renderKey"
                        class="flex flex-1 flex-col m-0 -ml-px -mt-px mr-px -mb-px border border-gray-200 shadow-none"
                        :style="'background-color: '+item.color"
                        :class="estimate.readonly?'':'hover:border-gray-400 hover:z-10'">
                        <template v-if="estimate.readonly">
                            <div
                                class="text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative whitespace-pre-wrap focus:border-blue-500 border-none focus:z-20 resize-none"
                                :style="'background-color: '+item.color+';border-color: '+item.color"
                                v-text="item.description">
                            </div>
                            <estimate-assembly-pinger :item="item" :estimate="estimate"
                                                      v-if="showPinger()"
                                                      :pinging="isPinging()"
                                                      class="top-2 right-2"></estimate-assembly-pinger>
                        </template>
                        <estimate-task-item-existing-search
                            class="flex-1 flex"
                            #default="{ close, open, query, setQuery }"
                            :estimate="estimate"
                            :item="item"
                            ref="existing-item-search"
                            :estimateClipboard="clipboardItemData"
                            @selected:existingItem="existingItemSelected"
                            :top="true"
                            v-else
                        >
                            <autoresizable-textarea :height="el => (el.scrollHeight + 2) + 'px'"
                                                    @textarea:heightAdjusted="() => {if (searcherOpen) { close();open(); }}"
                                                    ref="autoresizable-description"
                            >
                        <textarea
                            placeholder="Description..."
                            class="text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-10 resize-none"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            type="text"
                            ref="description"
                            :disabled="estimate.readonly"
                            v-model="item.description"
                            @input="setQuery($event.target.value);updateEstimate()"
                            @focus="setQuery($event.target.value);open();searcherOpen = true;"
                            @blur="searcherOpen=false;rebuildCollectedItems(estimate)"
                            @change="changeEstimate()"
                            rows="1"
                        ></textarea>
                            </autoresizable-textarea>
                            <estimate-assembly-pinger :item="item" :estimate="estimate"
                                                      v-if="showPinger()"
                                                      :pinging="isPinging()"
                                                      class="top-2 right-2"></estimate-assembly-pinger>
                        </estimate-task-item-existing-search>
                        <div
                            v-if="item.discount && estimate.readonly"
                            class="text-sm text-gray-400 m-0 px-2 -mt-2 pt-1.5 pb-1.5 whitespace-pre-wrap shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            v-text="item.discountComment?item.discountComment:' '"
                        ></div>
                        <autoresizable-textarea :height="el => (el.scrollHeight + 2) + 'px'"
                                                ref="autoresizable-discount-comment"
                                                v-else-if="(item.discount && !estimate.readonly) || editing==='discount' || editing==='discountRate'">
                        <textarea
                            placeholder="Discount explanation..."
                            class="text-sm text-gray-400 m-0 px-2 -mt-2 pt-1.5 pb-1.5 placeholder:text-gray-400 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none"
                            :style="'background-color: '+item.color+';border-color: '+item.color"
                            type="text"
                            ref="description"
                            :disabled="estimate.readonly"
                            v-model="item.discountComment"
                            @input="estimate.update()"
                            @change="estimate.addSnapshot()"
                            rows="1"
                        ></textarea>
                        </autoresizable-textarea>
                    </div>
                </div>

                <!-- Quantity. -->
                <div class="w-20 m-0 -ml-px flex">
                    <page-cell
                        class="text-sm -m-px px-2 py-1.5 border border-gray-200"
                        :style="'background-color: '+item.color"
                        placeholder="Qty"
                        :read-only="estimate.readonly"
                        v-model="quantity"
                        @input="updateQuantity"
                        @blur="updateAll()"
                        @change="updateAll();estimate.addSnapshot()"
                    ></page-cell>
                </div>

                <!-- Units of measure. -->
                <div class="w-20 m-0 -ml-px flex">
                    <page-cell
                        class="text-sm -m-px px-2 py-1.5 border border-gray-200"
                        :style="'background-color: '+item.color"
                        placeholder="Uom"
                        :read-only="estimate.readonly"
                        v-model="item.uom"
                        @input="estimate.update()"
                        @change="estimate.addSnapshot()"
                    ></page-cell>
                </div>

                <!-- Rate. -->
                <div class="w-28 m-0 -ml-px flex" v-if="item.quantity && isNull(item.bundle)">
                    <ui-dropdown placement="bottom-start" menu-class="p-3 w-72 flex z-50" class="flex w-full"
                                 :enabled="inView"
                                 trigger-class="flex w-full">
                        <template #default="{ open, close }">
                            <div
                                class="flex flex-1 flex-col m-0 -ml-px -mt-px mr-px -mb-px border border-gray-200 shadow-none hover:z-10"
                                :style="'background-color: '+item.color"
                                :class="estimate.readonly?'':'hover:border-gray-400'">
                                <page-cell
                                    currency
                                    class="text-right flex-1 text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none"
                                    :placeholder="item.quantity?(item.total/item.quantity):0 | toCurrency()"
                                    :style="'background-color: '+item.color+';border-color: '+item.color"
                                    :read-only="estimate.readonly || !isNull(bundle)"
                                    v-model="rate"
                                    @input="updateRate"
                                    @blur="updateAll()"
                                    @change="updateAll();estimate.addSnapshot()"
                                    @mouseenter="open"
                                    @mouseleave="close"
                                ></page-cell>
                                <page-cell
                                    v-if="item.discountPercent || editing==='discountRate' || editing==='discount'"
                                    currency
                                    class="text-right text-sm text-gray-400 m-0 -mt-2 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none"
                                    :style="'background-color: '+item.color+';border-color: '+item.color"
                                    :placeholder="item.quantity?(item.discount/item.quantity):0  | toCurrency()"
                                    :read-only="estimate.readonly || !isNull(bundle)"
                                    v-model="discountRate"
                                    @input="updateDiscountRate"
                                    @blur="updateAll()"
                                    @change="updateAll();estimate.addSnapshot()"
                                    @mouseenter="open"
                                    @mouseleave="close"
                                ></page-cell>
                            </div>
                        </template>
                        <template #menu>
                            <table class="w-full">
                                <tbody>
                                <tr>
                                    <td class="text-gray-600 text-sm" v-if="item.uom">Cost rate per {{ item.uom }}</td>
                                    <td class="text-gray-600 text-sm" v-else>Cost Rate</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold text-red-400"
                                        v-if="item.markupFixedValue">
                                        * {{
                                            (item.buildTotalMarkedUp - item.profit - item.overheads) / item.quantity | toCurrency
                                        }}
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold"
                                        v-else>
                                        {{ item.buildTotal / item.quantity | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Overheads <span
                                        class="text-gray-400">({{ estimate.overheads }}%)</span></td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ (item.overheads / item.quantity) | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Profit <span
                                        class="text-gray-400">({{ round(item.profitPercent, 2) }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ (item.profit / item.quantity) | toCurrency }}
                                    </td>
                                </tr>
                                <tr v-if="item.discountPercent || item.adjustment || item.mcd">
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr v-if="item.discountPercent">
                                    <td class="text-gray-600 text-sm">Less discount <span
                                        class="text-gray-400">({{
                                            round(item.discountPercent, 2)
                                        }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ 0 - (item.discount / item.quantity) | toCurrency }}
                                    </td>
                                </tr>
                                <tr v-if="item.adjustment">
                                    <td class="text-gray-600 text-sm">Less adjustment <span
                                        class="text-gray-400">({{
                                            round(estimate.adjustment, 2)
                                        }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ 0 - (item.adjustment / item.quantity) | toCurrency }}
                                    </td>
                                </tr>
                                <tr v-if="item.mcd">
                                    <td class="text-gray-600 text-sm">Less MCD <span
                                        class="text-gray-400">({{ estimate.mcd }}%)</span></td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ 0 - (item.mcd / item.quantity) | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Total</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ item.finalTotal / item.quantity | toCurrency }}
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </template>
                    </ui-dropdown>
                </div>
                <div class="w-28 m-0 -ml-px flex" v-else-if="item.quantity">
                    <ui-dropdown placement="bottom-start" menu-class="p-3 w-72 flex z-50" class="flex w-full"
                                 :enabled="inView"
                                 trigger-class="flex w-full">
                        <template #default="{ open, close }">
                            <div
                                class="flex flex-1 flex-col m-0 -ml-px -mt-px mr-px -mb-px border-none shadow-none"
                                :style="'background-color: '+item.color"
                            >
                                <page-cell
                                    currency
                                    class="text-right flex-1 text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none border-t border-r border-l border-gray-200 relative resize-none"
                                    :style="'background-color: '+item.color"
                                    :read-only="true"
                                    v-model="rate"
                                    @mouseenter="open"
                                    @mouseleave="close"
                                ></page-cell>
                                <page-cell
                                    v-if="item.discountPercent || editing==='discount' || editing==='discountRate'"
                                    currency
                                    class="text-right text-sm text-gray-400 m-0 -mt-2 px-2 pt-1.5 pb-1.5 shadow-none relative border-b border-r border-l border-gray-200 resize-none"
                                    :style="'background-color: '+item.color"
                                    :read-only="true"
                                    v-model="discountRate"
                                    @mouseenter="open"
                                    @mouseleave="close"
                                ></page-cell>
                            </div>
                        </template>
                        <template #menu>
                            <table class="w-full">
                                <tbody>
                                <tr>
                                    <td class="text-gray-600 text-sm" v-if="item.uom">Cost rate per {{ item.uom }}</td>
                                    <td class="text-gray-600 text-sm" v-else>Cost Rate</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold text-red-400"
                                        v-if="item.markupFixedValue">
                                        * {{
                                            (item.buildTotalMarkedUp - item.profit - item.overheads) / item.quantity | toCurrency
                                        }}
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold"
                                        v-else>
                                        {{ item.buildTotal / item.quantity | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Overheads <span
                                        class="text-gray-400">({{ estimate.overheads }}%)</span></td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ (item.overheads / item.quantity) | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Profit <span
                                        class="text-gray-400">({{ round(item.profitPercent, 2) }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ (item.profit / item.quantity) | toCurrency }}
                                    </td>
                                </tr>
                                <tr v-if="item.discountPercent || item.adjustment || item.mcd">
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr v-if="item.discountPercent">
                                    <td class="text-gray-600 text-sm">Less discount <span
                                        class="text-gray-400">({{
                                            round(item.discountPercent, 2)
                                        }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ 0 - (item.discount / item.quantity) | toCurrency }}
                                    </td>
                                </tr>
                                <tr v-if="item.adjustment">
                                    <td class="text-gray-600 text-sm">Less adjustment <span
                                        class="text-gray-400">({{
                                            round(estimate.adjustment, 2)
                                        }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ 0 - (item.adjustment / item.quantity) | toCurrency }}
                                    </td>
                                </tr>
                                <tr v-if="item.mcd">
                                    <td class="text-gray-600 text-sm">Less MCD <span
                                        class="text-gray-400">({{ estimate.mcd }}%)</span></td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ 0 - (item.mcd / item.quantity) | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Total</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ item.finalTotal / item.quantity | toCurrency }}
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </template>
                    </ui-dropdown>
                </div>
                <div class="w-28 m-0 -ml-px flex" v-else>
                    <page-cell
                        class="text-sm text-right -m-px px-2 py-1.5 border border-gray-200 text-red-400"
                        :style="'background-color: '+item.color"
                        disabled=""
                        value="???"
                    ></page-cell>
                </div>

                <!-- Total. -->
                <div class="w-28 m-0 -ml-px flex" v-if="isNull(item.bundle)">
                    <ui-dropdown placement="bottom-start" menu-class="p-3 w-72 flex z-50" class="flex w-full"
                                 :enabled="inView"
                                 trigger-class="flex w-full">
                        <template #default="{ open, close }">
                            <div
                                class="flex flex-1 flex-col m-0 -ml-px -mt-px mr-px -mb-px border border-gray-200 shadow-none hover:z-10"
                                :style="'background-color: '+item.color"
                                :class="estimate.readonly?'':'hover:border-gray-400'">
                                <page-cell
                                    currency
                                    class="text-right flex-1 text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none"
                                    :style="'background-color: '+item.color+';border-color: '+item.color"
                                    :placeholder="item.valueWithDefaultOHP | toCurrency()"
                                    :read-only="estimate.readonly || !isNull(bundle)"
                                    v-model="total"
                                    @input="updateTotal"
                                    @blur="updateAll()"
                                    @change="updateAll();estimate.addSnapshot()"
                                    @mouseenter="open"
                                    @mouseleave="close"
                                ></page-cell>
                                <page-cell
                                    v-if="item.discountPercent || editing==='discount' || editing==='discountRate'"
                                    currency
                                    class="text-right text-sm text-gray-400 m-0 -mt-2 px-2 pt-1.5 pb-1.5 shadow-none relative focus:border-blue-500 border-none focus:z-20 resize-none"
                                    :style="'background-color: '+item.color+';border-color: '+item.color"
                                    :placeholder="item.discount | toCurrency()"
                                    :read-only="estimate.readonly || !isNull(bundle)"
                                    v-model="discount"
                                    @input="updateDiscount"
                                    @blur="updateAll()"
                                    @change="updateAll();estimate.addSnapshot()"
                                    @mouseenter="open"
                                    @mouseleave="close"
                                ></page-cell>
                                <div class="absolute top-2 left-1 z-20" v-if="item.markupFixedValue">
                                    <i class="fas fa-star text-blue-600" style="font-size: 8px"></i>
                                </div>
                                <div class="absolute top-2 left-1 z-20"
                                     v-else-if="(round(item.buildTotal*(1+(estimate.profit+estimate.overheads)/100),2) > item.buildTotalMarkedUp)">
                                    <i class="fas fa-arrow-down text-red-600" style="font-size: 9px"></i>
                                </div>
                                <div class="absolute top-2 left-1 z-20"
                                     v-else-if="(round(item.buildTotal*(1+(estimate.profit+estimate.overheads)/100),2) < item.buildTotalMarkedUp)">
                                    <i class="fas fa-arrow-up text-green-600" style="font-size: 9px"></i>
                                </div>

                            </div>
                        </template>
                        <template #menu>
                            <table class="w-full">
                                <tbody>
                                <tr>
                                    <td class="text-gray-600 text-sm">Material Cost</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        <div
                                            v-if="item.markupFixedValue"
                                            class="text-red-400">
                                            ???
                                        </div>
                                        <div v-else>
                                            {{ item.materialBuildTotal | toCurrency }}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Labour Cost</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        <div
                                            v-if="item.markupFixedValue"
                                            class="text-red-400">
                                            ???
                                        </div>
                                        <div v-else>
                                            {{ item.labourBuildTotal | toCurrency }}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Fixed Cost</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        <div
                                            v-if="item.markupFixedValue"
                                            class="text-red-400">
                                            ???
                                        </div>
                                        <div v-else>
                                            {{ item.fixedCostBuildTotal | toCurrency }}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm font-semibold">Total Cost</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        <div
                                            v-if="item.markupFixedValue"
                                            class="text-red-400">
                                            * {{ item.buildTotalMarkedUp - item.profit - item.overheads | toCurrency }}
                                        </div>
                                        <div v-else>
                                            {{ item.buildTotal | toCurrency }}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Overheads <span
                                        class="text-gray-400">({{ estimate.overheads }}%)</span></td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ (item.overheads) | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Profit <span
                                        class="text-gray-400">({{ round(item.profitPercent, 2) }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ (item.profit) | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm font-semibold">Sub Total</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ (item.buildTotalMarkedUp) | toCurrency }}
                                    </td>
                                </tr>
                                <tr v-if="item.discountPercent || item.adjustment || item.mcd">
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr v-if="item.discountPercent">
                                    <td class="text-gray-600 text-sm">Less discount <span
                                        class="text-gray-400">({{
                                            round(item.discountPercent, 2)
                                        }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ 0 - (item.discount) | toCurrency }}
                                    </td>
                                </tr>
                                <tr v-if="item.adjustment">
                                    <td class="text-gray-600 text-sm">Less adjustment <span
                                        class="text-gray-400">({{
                                            round(estimate.adjustment, 2)
                                        }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ 0 - (item.adjustment) | toCurrency }}
                                    </td>
                                </tr>
                                <tr v-if="item.mcd">
                                    <td class="text-gray-600 text-sm">Less MCD <span
                                        class="text-gray-400">({{ estimate.mcd }}%)</span></td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ 0 - (item.mcd) | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm font-semibold">Grand Total</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ item.finalTotal | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Effective OHP <span
                                        class="text-gray-400">({{
                                            round(item.effectiveOHPPercent, 2)
                                        }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ item.effectiveOHP | toCurrency }}
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </template>
                    </ui-dropdown>
                </div>
                <div class="w-28 m-0 -ml-px flex" v-else>
                    <ui-dropdown placement="bottom-start" menu-class="p-3 w-72 flex z-50" class="flex w-full"
                                 trigger-class="flex w-full">
                        <template #default="{ open, close }">
                            <div
                                class="flex flex-1 flex-col m-0 -ml-px -mt-px mr-px -mb-px border-none shadow-none"
                                :style="'background-color: '+item.color">
                                <page-cell
                                    currency
                                    class="text-right flex-1 text-sm m-0 px-2 pt-1.5 pb-1.5 shadow-none border-t border-r border-l border-gray-200 relative r"
                                    :style="'background-color: '+item.color"
                                    :read-only="true"
                                    v-model="total"
                                    @mouseenter="open"
                                    @mouseleave="close"
                                ></page-cell>
                                <page-cell
                                    v-if="item.discountPercent || editing==='discount' || editing==='discountRate'"
                                    currency
                                    class="text-right text-sm text-gray-400 m-0 -mt-2 px-2 pt-1.5 pb-1.5 shadow-none relative border-b border-r border-l border-gray-200 resize-none"
                                    :style="'background-color: '+item.color"
                                    :read-only="true"
                                    v-model="discount"
                                    @mouseenter="open"
                                    @mouseleave="close"
                                ></page-cell>
                            </div>
                        </template>
                        <template #menu>
                            <table class="w-full">
                                <tbody>
                                <tr>
                                    <td class="text-gray-600 text-sm">Material Cost</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        <div
                                            v-if="item.markupFixedValue"
                                            class="text-red-400">
                                            ???
                                        </div>
                                        <div v-else>
                                            {{ item.materialBuildTotal | toCurrency }}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Labour Cost</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        <div
                                            v-if="item.markupFixedValue"
                                            class="text-red-400">
                                            ???
                                        </div>
                                        <div v-else>
                                            {{ item.labourBuildTotal | toCurrency }}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Fixed Cost</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        <div
                                            v-if="item.markupFixedValue"
                                            class="text-red-400">
                                            ???
                                        </div>
                                        <div v-else>
                                            {{ item.fixedCostBuildTotal | toCurrency }}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm font-semibold">Total Cost</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        <div
                                            v-if="item.markupFixedValue"
                                            class="text-red-400">
                                            * {{ item.buildTotalMarkedUp - item.profit - item.overheads | toCurrency }}
                                        </div>
                                        <div v-else>
                                            {{ item.buildTotal | toCurrency }}
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Overheads <span
                                        class="text-gray-400">({{ estimate.overheads }}%)</span></td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ (item.overheads) | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Profit <span
                                        class="text-gray-400">({{ round(item.profitPercent, 2) }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ (item.profit) | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm font-semibold">Sub Total</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ (item.buildTotalMarkedUp) | toCurrency }}
                                    </td>
                                </tr>
                                <tr v-if="item.discountPercent || item.adjustment || item.mcd">
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr v-if="item.discountPercent">
                                    <td class="text-gray-600 text-sm">Less discount <span
                                        class="text-gray-400">({{
                                            round(item.discountPercent, 2)
                                        }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ 0 - (item.discount) | toCurrency }}
                                    </td>
                                </tr>
                                <tr v-if="item.adjustment">
                                    <td class="text-gray-600 text-sm">Less adjustment <span
                                        class="text-gray-400">({{
                                            round(estimate.adjustment, 2)
                                        }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ 0 - (item.adjustment) | toCurrency }}
                                    </td>
                                </tr>
                                <tr v-if="item.mcd">
                                    <td class="text-gray-600 text-sm">Less MCD <span
                                        class="text-gray-400">({{ estimate.mcd }}%)</span></td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ 0 - (item.mcd) | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="2">
                                        <div class="border-b border-gray-200 my-1"></div>
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm font-semibold">Grand Total</td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ item.finalTotal | toCurrency }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-gray-600 text-sm">Effective OHP <span
                                        class="text-gray-400">({{
                                            round(item.effectiveOHPPercent, 2)
                                        }}%)</span>
                                    </td>
                                    <td class="text-right pl-4 text-sm font-open-sans tabular-nums font-semibold">
                                        {{ item.effectiveOHP | toCurrency }}
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </template>
                    </ui-dropdown>
                </div>

                <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t pl-1 pr-1">
                    <button v-if="isNull(item.bundle)"
                            @click="$modals.open('estimate-task-item-build', { item, estimate, store })"
                            class="text-gray-300 text-xs hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded mt-0.5">
                        <i class="fas fa-hammer"></i>
                    </button>
                    <button v-else
                            @click="() => {item.bundleOpened = !item.bundleOpened; estimate.lastItemKey = item.id+'-'+itemKey++; estimate.update();}">
                        <i class="fas fa-layer-group text-blue-600"
                           :class="item.bundleOpened?'opacity-50 hover:opacity-100':'opacity-25 hover:opacity-75'"
                           style="font-size: 12px"
                        ></i>
                    </button>
                </div>
                <!-- Dropdown. -->
                <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t pl-1 pr-1">
                    <ui-dropdown menu-class="p-3 w-56 z-50" v-if="isNull(item.bundle)" :enabled="inView"
                                 :key="clipboardKey"
                                 :close-on-mouse-out-scroll="true">
                        <template #default="{ toggle }">
                            <button @click="toggle"
                                    class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                                <i class="fa fa-ellipsis-h"></i>
                            </button>
                        </template>
                        <template #menu="{ close }">
                            <ui-dropdown-item blue icon="fas fa-layer-group text-blue-400"
                                              @mousedown="convertIntoBundle(item); close();"
                                              v-if="!estimate.readonly">
                                Convert into a bundle
                            </ui-dropdown-item>
                            <ui-dropdown-item icon="fas fa-tint"
                                              @mouseleave="(e) => {$refs['itemcolormenu'].close(e)}"
                                              @mouseenter="openColorSubmenu"
                                              ref="itemcolor"
                                              :submenu="true"
                                              v-if="!estimate.readonly"
                            >
                                Set item color
                                <div class="fixed" style="width:auto;height:auto;overflow: visible"
                                     :ref="'itemcolorrelative'">
                                    <vue-context :ref="'itemcolormenu'" :autoFocus="false"
                                                 class="inline pb-0 focus:outline-none focus:ring-0 z-30"
                                                 :close-on-click="false" :close-on-scroll="false">
                                        <div :id="'itemcolormenu'"
                                             class="p-3 w-48 z-30 bg-white rounded-lg border border-gray-300 shadow-lg">
                                            <div
                                                v-for="(index,key) in ([0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19])"
                                                class="w-8 inline-block items-center p-1.5 border border-none rounded-lg"
                                                :key="index">
                                                <button
                                                    class="w-6 h-6 rounded-lg flex-none mt-0.5 mr-2 inline-block border border-gray-200 hover:border-gray-400"
                                                    :style="'background-color: '+color(key)"
                                                    @mousedown="setColor(item,color(key))">
                                                </button>
                                            </div>
                                        </div>
                                    </vue-context>
                                </div>
                            </ui-dropdown-item>
                            <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                            <ui-dropdown-item icon="fas fa-calculator"
                                              @mousedown="$modals.open('estimate-item-cost-list', { item, task, section, estimate })">
                                See Cost Breakdown
                            </ui-dropdown-item>
                            <ui-dropdown-divider></ui-dropdown-divider>
                            <ui-dropdown-item icon="fas fa-cut" @mousedown="cutItem();close();"
                                              v-if="!estimate.readonly">Cut Item
                            </ui-dropdown-item>
                            <ui-dropdown-item icon="fas fa-copy" @mousedown="copyItem();close();">Copy Item
                            </ui-dropdown-item>
                            <ui-dropdown-item icon="fas fa-paste"
                                              v-if="!estimate.readonly && (clipboardItemData[0])"
                                              @mousedown="pasteItem(clipboardItemData[0],item);close();">
                                Paste Item
                            </ui-dropdown-item>
                            <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                            <ui-dropdown-item icon="fas fa-hammer"
                                              v-if="!estimate.readonly"
                                              @mousedown="editAssembly();close();">
                                Edit Assembly
                            </ui-dropdown-item>
                            <ui-dropdown-item icon="far fa-window-restore"
                                              v-if="!estimate.readonly"
                                              @mousedown="findAndReplace(close)">
                                Find &amp; Replace
                            </ui-dropdown-item>
                            <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                            <ui-dropdown-item ref="paste1item"
                                              @mouseleave="closePasteSubMenu(1)"
                                              @mouseenter="openPasteSubMenu(1)"
                                              v-if="!estimate.readonly"
                                              icon="fas fa-clipboard"
                                              :submenu="true"
                            >Library
                                <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                                     :ref="'paste1relative'">
                                    <vue-context :ref="'paste1menu'" :autoFocus="false"
                                                 class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                                 :close-on-click="false">
                                        <div :id="'paste1menu'"
                                             class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg">
                                            <ui-dropdown-item icon="fas fa-plus"
                                                              @mousedown="addToLibrary(item);close();">
                                                Add Item to Library
                                            </ui-dropdown-item>
                                            <ui-dropdown-divider
                                                v-if="!estimate.readonly && (clipboardItemData.length > 1)"></ui-dropdown-divider>
                                            <div class="max-h-96 overflow-x-auto">
                                                <ui-dropdown-item :ref="'paste999'+groupIndex+'item'"
                                                                  v-for="(group,groupIndex) in groupedclipboardItemData()"
                                                                  :key="'paste999'+groupIndex"
                                                                  v-if="group.name"
                                                                  @mouseleave="closePasteSubMenu('999'+groupIndex)"
                                                                  @mouseenter="openPasteSubMenu('999'+groupIndex)"
                                                                  :submenu="true"
                                                                  icon=" "
                                                >{{ group.name }}
                                                    <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                                                         :ref="'paste999'+groupIndex+'relative'">
                                                        <vue-context :ref="'paste999'+groupIndex+'menu'"
                                                                     :autoFocus="false"
                                                                     class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                                                     :close-on-click="false" :close-on-scroll="false">
                                                            <div :id="'paste999'+groupIndex+'menu'"
                                                                 class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg max-h-96 overflow-x-auto">
                                                                <ui-dropdown-item icon="fas fa-paste"
                                                                                  button-class="truncate"
                                                                                  class="w-full truncate flex"
                                                                                  v-for="(tem,i) in group.items"
                                                                                  :key="'paste999'+groupIndex+'-'+i"
                                                                                  @mousedown="pasteItem(tem.data,item);close();">
                                                                    <div class="text-xs text-blue-500 truncate">{{
                                                                            tem.name
                                                                        }}
                                                                    </div>
                                                                </ui-dropdown-item>
                                                            </div>
                                                        </vue-context>
                                                    </div>
                                                </ui-dropdown-item>
                                                <ui-dropdown-item icon="fas fa-paste"
                                                                  button-class="truncate"
                                                                  class="w-full truncate flex"
                                                                  v-for="(tem,i) in groupedclipboardItemData().find((a) => a.name === '').items"
                                                                  :key="'paste1-'+i"
                                                                  @mousedown="pasteItem(tem.data,item);close();">
                                                    <div class="text-xs text-blue-500 truncate">{{ tem.name }}</div>
                                                </ui-dropdown-item>
                                            </div>
                                        </div>
                                    </vue-context>
                                </div>
                            </ui-dropdown-item>
                            <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                            <ui-dropdown-item v-if="!estimate.readonly" icon="fas fa-trash" danger
                                              @mousedown="deleteItem();close();">Delete
                            </ui-dropdown-item>
                        </template>
                    </ui-dropdown>
                    <ui-dropdown menu-class="p-3 w-56 z-50" v-else :close-on-mouse-out-scroll="true" :enabled="inView">
                        <template #default="{ toggle }">
                            <button @click="toggle"
                                    class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                                <i class="fa fa-ellipsis-h"></i>
                            </button>
                        </template>
                        <template #menu="{ close }">
                            <ui-dropdown-item blue icon="fas fa-layer-group text-blue-400"
                                              @mousedown="convertToItems(item);close()"
                                              v-if="!estimate.readonly">
                                Convert back to items
                            </ui-dropdown-item>
                            <ui-dropdown-item icon="fas fa-tint"
                                              @mouseleave="(e) => {$refs['itemcolormenu'].close(e)}"
                                              @mouseenter="openColorSubmenu"
                                              ref="itemcolor"
                                              :submenu="true"
                                              v-if="!estimate.readonly"
                            >
                                Set item color
                                <div class="fixed" style="width:auto;height:auto;overflow: visible"
                                     :ref="'itemcolorrelative'">
                                    <vue-context :ref="'itemcolormenu'" :autoFocus="false"
                                                 class="inline pb-0 focus:outline-none focus:ring-0 z-30"
                                                 :close-on-click="false" :close-on-scroll="false">
                                        <div :id="'itemcolormenu'"
                                             class="p-3 w-48 z-30 bg-white rounded-lg border border-gray-300 shadow-lg">
                                            <div
                                                v-for="(index,key) in ([0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19])"
                                                class="w-8 inline-block items-center p-1.5 border border-none rounded-lg"
                                                :key="index">
                                                <button
                                                    class="w-6 h-6 rounded-lg flex-none mt-0.5 mr-2 inline-block border border-gray-200 hover:border-gray-400"
                                                    :style="'background-color: '+color(key)"
                                                    @mousedown="setColor(item,color(key))">
                                                </button>
                                            </div>
                                        </div>
                                    </vue-context>
                                </div>
                            </ui-dropdown-item>
                            <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                            <ui-dropdown-item icon="fas fa-cut" @mousedown="cutItem();close();"
                                              v-if="!estimate.readonly">Cut Item
                            </ui-dropdown-item>
                            <ui-dropdown-item icon="fas fa-copy" @mousedown="copyItem();close();">Copy Item
                            </ui-dropdown-item>
                            <ui-dropdown-item icon="fas fa-paste"
                                              v-if="!estimate.readonly && (clipboardItemData[0])"
                                              @mousedown="pasteItem(clipboardItemData[0],item);close();">
                                Paste Item
                            </ui-dropdown-item>
                            <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                            <ui-dropdown-item ref="paste1item"
                                              @mouseleave="closePasteSubMenu(1)"
                                              @mouseenter="openPasteSubMenu(1)"
                                              v-if="!estimate.readonly"
                                              icon="fas fa-clipboard"
                                              :submenu="true"
                            >Library
                                <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                                     :ref="'paste1relative'">
                                    <vue-context :ref="'paste1menu'" :autoFocus="false"
                                                 class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                                 :close-on-click="false" :close-on-scroll="false">
                                        <div :id="'paste1menu'"
                                             class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg">
                                            <ui-dropdown-item icon="fas fa-plus"
                                                              @mousedown="addToLibrary(item);close();">
                                                Add Item to Library
                                            </ui-dropdown-item>
                                            <ui-dropdown-divider
                                                v-if="!estimate.readonly && (clipboardItemData.length > 1)"></ui-dropdown-divider>
                                            <div class="max-h-96 overflow-x-auto">
                                                <ui-dropdown-item :ref="'paste999'+groupIndex+'item'"
                                                                  v-for="(group,groupIndex) in groupedclipboardItemData()"
                                                                  :key="'paste999'+groupIndex"
                                                                  v-if="group.name"
                                                                  @mouseleave="closePasteSubMenu('999'+groupIndex)"
                                                                  @mouseenter="openPasteSubMenu('999'+groupIndex)"
                                                                  :submenu="true"
                                                                  icon=" "
                                                >{{ group.name }}
                                                    <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                                                         :ref="'paste999'+groupIndex+'relative'">
                                                        <vue-context :ref="'paste999'+groupIndex+'menu'"
                                                                     :autoFocus="false"
                                                                     class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                                                     :close-on-click="false" :close-on-scroll="false">
                                                            <div :id="'paste999'+groupIndex+'menu'"
                                                                 class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg max-h-96 overflow-x-auto">
                                                                <ui-dropdown-item icon="fas fa-paste"
                                                                                  button-class="truncate"
                                                                                  class="w-full truncate flex"
                                                                                  v-for="(tem,i) in group.items"
                                                                                  :key="'paste999'+groupIndex+'-'+i"
                                                                                  @mousedown="pasteItem(tem.data,item);close();">
                                                                    <div class="text-xs text-blue-500 truncate">
                                                                        {{ tem.name }}
                                                                    </div>
                                                                </ui-dropdown-item>
                                                            </div>
                                                        </vue-context>
                                                    </div>
                                                </ui-dropdown-item>
                                                <ui-dropdown-item icon="fas fa-paste"
                                                                  button-class="truncate"
                                                                  class="w-full truncate flex"
                                                                  v-for="(tem,i) in groupedclipboardItemData().find((a) => a.name === '').items"
                                                                  :key="'paste1-'+i"
                                                                  @mousedown="pasteItem(tem.data,item);close();">
                                                    <div class="text-xs text-blue-500 truncate">{{ tem.name }}</div>
                                                </ui-dropdown-item>
                                            </div>
                                        </div>
                                    </vue-context>
                                </div>
                            </ui-dropdown-item>
                            <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                            <ui-dropdown-item v-if="!estimate.readonly" icon="fas fa-trash" danger
                                              @mousedown="deleteItem();close();">Delete
                            </ui-dropdown-item>
                        </template>
                    </ui-dropdown>
                </div>
            </div>
            <!-- Bundled Items -->
            <draggable :list="bundle" draggable=".draggable" handle=".handle" group="items"
                       v-if="!isNull(item.bundle) && item.bundleOpened"
                       @change="updateBundle">
                <estimate-task-bundle-item
                    v-for="(bundleItem,key) in bundle"
                    :key="'bundle|' + key"
                    :parentItem="item"
                    :item="bundleItem"
                    :task="task"
                    :section="section"
                    :estimate="estimate"
                    :store="store"
                    :multimode="multimode"
                    @ping="(e) => $emit('ping',e)"
                    :pinging="pinging"
                    ref="bundleItems"
                    :inView="inView"
                    :estimate-clipboard="clipboardItemData"
                ></estimate-task-bundle-item>
                <div class="flex text-blue-600 " :style="'background-color: '+item.color"
                     v-if="!estimate.readonly">
                    <div class="flex-1 flex m-0 -ml-px relative" :style="'background-color: '+item.color">
                        <div
                            class="flex flex-1 flex-row m-0 -ml-px -mt-px mr-px -mb-px border border-dashed border-gray-200 shadow-none hover:border-gray-400 hover:z-10"
                            :style="'background-color: '+item.color">
                            <!-- Plus handle. -->
                            <div class="w-6 flex-shrink-0 self-start pt-1 pl-1 my-px">
                                <div class="w-5 h-5 flex items-center">
                                    <i class="fa fa-plus text-xs text-gray-300 pl-1 pt-1"></i>
                                </div>
                            </div>
                            <div class="flex-1">
                                <page-cell
                                    :style="'background-color: '+item.color"
                                    class="text-sm mt-0 mb-px ml-0 px-2 py-1.5 border-none border-gray-200 bg-white"
                                    :placeholder="'Add a new bundle item...'"
                                    v-model="newItem.description"
                                    ref="addDescription"
                                    @input="addBundleItem()"
                                ></page-cell>
                            </div>
                        </div>
                    </div>
                    <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t border-dashed pl-1 pr-1">
                    </div>
                    <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t border-dashed pl-1 pr-1">
                    </div>

                </div>
            </draggable>
        </div>
    </div>
</template>

<script>

import draggable from 'vuedraggable'
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import {cloneDeep, isNull} from "lodash";
import {resolveNumericText, toCurrency} from "../../../utils";
import AutoresizableTextarea from "../../ui/AutoresizableTextarea";
import EstimateTaskBundleItem from "./EstimateTaskBundleItem.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import PageCell from "../../ui/PageCell.vue";
import * as math from 'mathjs';
import remoteStorage from "../../../utils/remoteStorage";
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";
import EstimateTaskItemMaterialSearch from "./build/EstimateTaskItemMaterialSearch.vue";
import EstimateTaskItemExistingSearch from "./EstimateTaskItemExistingSearch.vue";
import {Estimate} from "@models";
import EstimateAssemblyPinger from "./EstimateAssemblyPinger.vue";

export default {
    components: {
        EstimateAssemblyPinger,
        EstimateTaskItemExistingSearch,
        EstimateTaskItemMaterialSearch,
        UiDropdownDivider,
        PageCell, UiDropdown, draggable, EstimateTaskBundleItem, AutoresizableTextarea, UiDropdownItem
    },
    props: {
        estimate: Object,
        section: Object,
        task: Object,
        item: Object,
        store: Object,
        multimode: false,
        pinging: null,
        clipboardKey: Number,
        clipboardItemData: Array,
    },
    data() {
        return {
            quantity: this.item.quantity ? this.item.quantity : '',
            total: this.item.buildTotalMarkedUp,
            discount: toCurrency(0 - Math.abs(this.item.discount)),
            adjustment: toCurrency(0 - Math.abs(this.item.adjustment)),
            mcd: toCurrency(0 - Math.abs(this.item.mcd)),
            rate: math.round(this.item.buildTotalMarkedUp / this.item.quantity, 2),
            discountRate: toCurrency(0 - (Math.abs(this.item.discount) / this.item.quantity)),
            adjustmentRate: toCurrency(0 - (Math.abs(this.item.adjustment) / this.item.quantity)),
            mcdRate: toCurrency(0 - (Math.abs(this.item.mcd) / this.item.quantity)),
            editing: false,
            resolveNumericText,
            bundle: this.item.bundle,
            itemKey: 0,
            renderKey: 0,
            newItem: {
                description: null,
            },
            searcherOpen: false,
            highlighted: false,
            selected: false,
            distance: {top: 0, bottom: 0},
            inView: this.item.isNew,
        }
    },
    watch: {
        item: {
            handler(newValue, oldValue) {
                if (this.editing !== 'quantity') {
                    this.quantity = this.item.quantity ? this.item.quantity : ''
                }
                if (this.editing !== 'total') {
                    this.total = this.item.buildTotalMarkedUp
                }
                if (this.editing !== 'rate') {
                    this.rate = math.round((this.item.quantity ? (this.item.buildTotalMarkedUp / this.item.quantity) : 0), 2)
                }
                if (this.editing !== 'discount') {
                    this.discount = toCurrency(0 - Math.abs(this.item.discount))
                }
                if (this.editing !== 'discountRate') {
                    this.discountRate = toCurrency(this.item.quantity ? (0 - Math.abs(this.item.discount / this.item.quantity)) : 0)
                }
                this.mcd = toCurrency(0 - Math.abs(this.item.mcd))
                this.adjustmentRate = toCurrency(0 - (Math.abs(this.item.adjustment) / this.item.quantity))
                this.adjustment = toCurrency(0 - (Math.abs(this.item.adjustment)))
                this.mcdRate = toCurrency(0 - (Math.abs(this.item.mcd) / this.item.quantity))
                this.mcd = toCurrency(0 - (Math.abs(this.item.mcd)))
                this.bundle = this.item.bundle
            },
            deep: true,
        },
        "estimate.collectionBuildUUID": {
            handler(newValue, oldValue) {
                this.renderKey = this.renderKey + 1
            },
            deep: true,
        }
    },
    methods: {
        showPinger() {
            let m = this.estimate.collectedItems.items.get(Estimate.itemAsKey(this.item))
            return !this.item.bundle && (m && (m.quantity > 1))
        },
        isPinging() {
            return this.pinging === Estimate.itemAsKey(this.item)
        },
        removeSelected() {
            this.selected = false;
        },
        addSelected() {
            this.selected = true;
        },
        highlight() {
            if (this.multimode) {
                this.highlighted = true
            }
            if (!this.item.bundle) {
                this.$emit('ping', Estimate.itemAsKey(this.item))
            }
        },
        unhighlight() {
            if (this.multimode) {
                this.highlighted = false
            }
            if (!this.item.bundle) {
                this.$emit('ping', null)
            }
        },
        groupedclipboardItemData() {
            let ret = [{name: '', items: []}];
            this.clipboardItemData.forEach((i, index) => {
                if (index > 0) {
                    let groupName = ''
                    if (i.group) {
                        groupName = i.group
                    }
                    let group = ret.find((a) => a.name === groupName)
                    if (!group) {
                        group = {name: groupName, items: []}
                        ret.push(group)
                    }
                    group.items.push(i)
                }
            })
            return ret
        },
        addToLibrary(item) {
            this.$modals.open(
                'estimate-add-item-to-library',
                {
                    name: (item.description + ' - ' + item.reference + ' (' + toCurrency(item.total) + ')').replace(/(\r\n|\n|\r)/gm, ""),
                    title: `Add Item to Library`,
                    button: 'Add Item',
                    handle: (name, group) => {
                        this.clipboardItemData.push({
                            name: name,
                            group: group,
                            data: cloneDeep(item)
                        })
                        remoteStorage.setItem('estimate-item-clipboard', JSON.stringify(this.clipboardItemData))
                        remoteStorage.setItem('estimate-clipboard-view', 'item')
                    }
                }
            )
        },
        closePasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].close()
            } else {
                this.$refs[p].close()
            }
        },
        openPasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].open(new window.MouseEvent('mouse'))
            } else {
                this.$refs[p].open(new window.MouseEvent('mouse'))
            }
            this.positionPasteMenu(num)
        },
        pasteItem(item, at) {
            remoteStorage.setItem('estimate-clipboard-view', 'item');
            let num = this.task.items.indexOf(at);
            this.task.items.splice(num, 0, cloneDeep(item));
            this.estimate.update();
            this.estimate.addSnapshot();
            this.$emit('reflow')
        },
        positionPasteMenu(num) {
            this.$nextTick(() => {
                if (this.$refs["paste" + num + "relative"][0]) {
                    this.$refs["paste" + num + "relative"][0].setAttribute('style', '')
                } else {
                    this.$refs["paste" + num + "relative"].setAttribute('style', '')
                }
                this.$nextTick(() => {
                    let opener, box
                    if (this.$refs["paste" + num + "item"][0]) {
                        opener = this.$refs["paste" + num + "item"][0].$refs.submenu.getBoundingClientRect()
                    } else {
                        opener = this.$refs["paste" + num + "item"].$refs.submenu.getBoundingClientRect()
                    }
                    if (this.$refs["paste" + num + "menu"][0]) {
                        box = this.$refs["paste" + num + "menu"][0].$el.getBoundingClientRect()
                    } else {
                        box = this.$refs["paste" + num + "menu"].$el.getBoundingClientRect()
                    }
                    let left = Math.min(opener.x - box.x - 10, window.innerWidth - (box.right) - 17);
                    let top = Math.min(opener.y - box.y - 10, window.innerHeight - (box.bottom) - 28);
                    if (this.$refs["paste" + num + "relative"][0]) {
                        this.$refs["paste" + num + "relative"][0].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    } else {
                        this.$refs["paste" + num + "relative"].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    }
                })
            })
        },
        existingItemSelected(item) {
            if (Estimate.itemAsKey(this.item) !== Estimate.itemAsKey(item.item)) {
                let q = this.item.quantity
                let ref = this.item.reference
                Object.assign(this.item, cloneDeep(item.item))
                this.item.reference = ref
                // clear any discounts
                this.item.discountPercent = 0
                this.item.discount = 0
                this.item.discountComment = ''
                this.quantity = q
                this.updateQuantity()
                this.estimate.addSnapshot()
                this.$emit('reflow')
            }
        },
        color(x) {
            return this.estimate.colors(x);
        },
        setColor(item, color) {
            item.color = color
            this.estimate.update()
            this.estimate.addSnapshot()
        },
        openColorSubmenu(e) {
            this.$refs["itemcolormenu"].open(new window.MouseEvent('mouse'))
            this.$nextTick(() => {
                this.$refs["itemcolorrelative"].setAttribute('style', '')
                this.$nextTick(() => {
                    const opener = this.$refs.itemcolor.$refs.submenu.getBoundingClientRect()
                    const box = this.$refs["itemcolor"].$el.getBoundingClientRect()
                    let left = Math.min(opener.x - box.x - 40, window.innerWidth - (box.right) - 17);
                    let top = Math.min(opener.y - box.y - 16, window.innerHeight - (box.bottom) - 28);
                    this.$refs["itemcolorrelative"].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                })
            })
        },
        isNull,
        round(a, b) {
            if (a) {
                return math.round(a, 0 + b);
            }
            return 0;
        },
        addBundleItem() {
            this.item.bundle.push({
                reference: '',
                defaultReference: '',
                description: this.newItem.description,
                quantity: 0,
                uom: '',
                rate: 0,
                total: 0,
                discount: 0,
                discountComment: '',
                markup: 0,
                materials: [],
                labour: [],
                fixedCosts: [],
                buildTotal: 0,
                buildTotalMarkedUp: 0,
                color: '#FFFFFF',
                bundle: null,
            });
            this.$nextTick(() => {
                this.$refs.bundleItems[this.$refs.bundleItems.length - 1].$refs.description.focus()
                this.newItem.description = ''
                this.estimate.update()
            })
        },
        updateBundle(e) {
            this.estimate.update()
            this.estimate.addSnapshot()
        },
        convertIntoBundle(item) {
            this.item.bundle = [cloneDeep(item)];
            this.item.bundleOpened = true;
            this.item.description = 'Bundle name...'
            this.estimate.update()
            this.estimate.addSnapshot()
            this.itemKey++;
        },
        convertToItems(item) {
            const i = this.task.items.indexOf(item);
            this.task.items.splice(i, 1, ...this.item.bundle)
            this.estimate.updateReferences();
            this.item.bundle.forEach((i) => i.reference = i.defaultReference)
            this.estimate.update()
            this.estimate.addSnapshot()
            this.itemKey++;
        },
        isNumeric(num) {
            return (typeof (num) === 'number' || typeof (num) === "string" && num.trim() !== '') && !isNaN(num);
        },
        cutItem() {
            this.clipboardItemData[0] = cloneDeep(this.item)
            remoteStorage.setItem('estimate-item-clipboard', JSON.stringify(this.clipboardItemData))
            remoteStorage.setItem('estimate-clipboard-view', 'item');
            this.task.items = this.task.items.filter((item) => item !== this.item)
            this.estimate.update()
            this.estimate.addSnapshot()
            this.$emit('reflow')
        },
        copyItem() {
            this.clipboardItemData[0] = cloneDeep(this.item)
            remoteStorage.setItem('estimate-item-clipboard', JSON.stringify(this.clipboardItemData))
            remoteStorage.setItem('estimate-clipboard-view', 'item');
        },
        deleteItem() {
            this.task.items = this.task.items.filter((item) => item !== this.item)
            this.estimate.update()
            this.estimate.addSnapshot()
            this.$emit('reflow')
        },
        updateTotal(val) {
            this.editing = 'total'
            let toMatch = this.item.valueWithDefaultOHP
            let value
            if (val === '') {
                value = toMatch;
            } else {
                value = math.round(this.resolveNumericText(val), 3);
            }
            let clearFixed = value === toMatch;
            if (this.item.buildTotal) {
                this.item.markupFixedValue = null
                let markup = value - this.item.buildTotal;
                this.item.profitPercent = clearFixed ? this.estimate.profit : (this.item.buildTotal ? (markup / this.item.buildTotal) * 100 : 0) - this.estimate.overheads;
                this.item.fixedProfit = !clearFixed
            } else {
                this.item.markup = 0;
                this.item.markupFixedValue = value
            }
            this.estimate.update()
        },
        updateDiscount(val) {
            this.editing = 'discount'
            this.item.discountPercent = Math.min(100, Math.max(0, (math.round(this.resolveNumericText(val, true), 2)) / this.item.buildTotalMarkedUp * 100))
            this.estimate.update()
            this.discountRate = toCurrency(0 - Math.abs(math.round((this.quantity ? (this.item.discount / this.quantity) : 0), 2)))
        },
        updateRate(val) {
            this.editing = 'rate'
            let clearFixed = false;
            if (val === '') {
                val = this.item.buildTotal * (1 + (this.estimate.profit + this.estimate.overheads) / 100) / this.item.quantity
                clearFixed = true;
            }
            let value = math.round(this.resolveNumericText(val), 3);
            if (this.item.buildTotal) {
                this.item.markupFixedValue = null
                let markup = (value * this.item.quantity) - this.item.buildTotal;
                this.item.profitPercent = (this.item.buildTotal ? (markup / this.item.buildTotal) * 100 : 0) - this.estimate.overheads;
                this.item.fixedProfit = !clearFixed
            } else {
                this.item.markup = 0;
                this.item.markupFixedValue = value * this.item.quantity
            }
            this.estimate.update()
        },
        updateDiscountRate(val) {
            this.editing = 'discountRate'
            let value = math.round(this.resolveNumericText(val, true), 2);
            this.item.discountPercent = Math.min(100, Math.max(0, (value * this.item.quantity) / this.item.buildTotalMarkedUp * 100))
            this.estimate.update()
        },
        updateQuantity() {
            this.editing = 'quantity'
            let value = math.round(this.resolveNumericText(this.quantity), 3);
            this.estimate.setItemQuantity(this.item, isNaN(value) ? 0 : value);
            this.estimate.update()
        },
        updateAll(val) {
            this.editing = false
            this.quantity = this.item.quantity ? this.item.quantity : ''
            this.total = this.item.buildTotalMarkedUp
            this.rate = math.round((this.quantity ? (this.total / this.quantity) : 0), 2)
            this.total = math.round(this.total, 2)
            this.discount = toCurrency(0 - Math.abs(this.item.discount))
            this.discountRate = toCurrency(0 - Math.abs(math.round((this.quantity ? (this.item.discount / this.quantity) : 0), 2)))
        },
        getItemColor() {
            return this.estimate.collectedItems.items.has(Estimate.itemAsKey(this.item)) ? this.estimate.collectedItems.items.get(Estimate.itemAsKey(this.item)).color : ''
        },
        editAssembly() {
            this.$modals.open('estimate-task-item-build', {
                item: this.item,
                editAsAssembly: true,
                estimate: this.estimate,
                store: this.store
            })
        },
        findAndReplace(close) {
            this.$modals.open('estimate-task-item-find-and-replace', {
                estimate: this.estimate,
                section: this.section,
                task: this.task,
                item: this.item,
                close: close,
                handle: function (data) {
                    data.items.forEach((item) => !data.partial ? item.description = data.replace : item.description = item.description.replaceAll(data.search, data.replace));
                    this.estimate.update()
                    this.estimate.addSnapshot()
                    this.$emit('reflow')
                }
            })
        },
        setIsInView(val) {
            if (this.inView !== val) {
                this.inView = val
                return true
            }
            return false
        },
        changeEstimate() {
            let loc = document.getElementById('ui-crud-main-body').scrollTop;
            this.estimate.addSnapshot()
            document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            this.$nextTick(() => {
                document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            })
        },
        updateEstimate() {
            let loc = document.getElementById('ui-crud-main-body').scrollTop;
            this.estimate.update()
            document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            this.$nextTick(() => {
                document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            })
        },
        rebuildCollectedItems() {
            let loc = document.getElementById('ui-crud-main-body').scrollTop;
            this.estimate.buildCollectedItems();
            document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            this.$nextTick(() => {
                document.getElementById('ui-crud-main-body').scrollTo({top: loc, left: 0, behavior: 'instant'});
            })
        }
    },
    mounted() {
    },
}
</script>
